import { useCallback, useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useSEO } from '@/lib/useSEO';
import { FileText, Save, RefreshCcw, AlertTriangle, CheckCircle2, History, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { downloadXlsx, downloadCsv } from '@/lib/download';

/**
 * Panel admin para editar los 4 documentos legales sin tocar código.
 * - Edita in-place (PUT /api/admin/legal/:slug): guarda title/body/version/
 *   is_provisional sobre la versión current sin crear una nueva.
 * - Publica nueva versión (POST /api/admin/legal): retira la current y crea
 *   una nueva versión — usar cuando el abogado entregue el copy final.
 *
 * Uso previsto:
 * 1) Mientras esté en revisión jurídica → editar in-place manteniendo
 *    `is_provisional=true` y el sufijo `-provisional` en la versión.
 * 2) Cuando el abogado apruebe → subir version a `1.0`, desmarcar provisional
 *    y publicar como nueva versión (POST) para preservar historia.
 */
const SLUGS = [
  { slug: 'terms', label: 'Términos y Condiciones', publicPath: '/terminos' },
  { slug: 'purchase', label: 'Política de Compras', publicPath: '/politicas' },
  { slug: 'returns', label: 'Política de Devoluciones', publicPath: '/devoluciones' },
  { slug: 'privacy', label: 'Política de Privacidad', publicPath: '/privacidad' },
  { slug: 'shipping', label: 'Tarifas, Envíos y Subsidios', publicPath: '/politica-de-envios' },
];

export default function AdminLegalDocs() {
  const [activeSlug, setActiveSlug] = useState('terms');
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', version: '', is_provisional: true });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [exportFilters, setExportFilters] = useState({ date_from: '', date_to: '' });

  useSEO({
    title: 'Documentos legales | Administración',
    description: 'Editar Términos, Política de Compras, Devoluciones y Privacidad de AutoMarket Colombia.',
    canonical: '/admin/documentos-legales',
  });

  const load = useCallback(async () => {
    setDoc(null);
    setHistory([]);
    try {
      const [{ data }, { data: h }] = await Promise.all([
        api.get(`/legal/${activeSlug}`),
        api.get(`/admin/legal/${activeSlug}/history`).catch(() => ({ data: { items: [] } })),
      ]);
      setDoc(data);
      setHistory(h.items || []);
      setForm({
        title: data.title || '',
        body: data.body || '',
        version: data.version || '',
        is_provisional: !!data.is_provisional,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el documento');
    }
  }, [activeSlug]);

  useEffect(() => { load(); }, [load]);

  const saveInline = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/legal/${activeSlug}`, {
        title: form.title,
        body: form.body,
        version: form.version,
        is_provisional: form.is_provisional,
      });
      toast.success('Cambios guardados (versión current actualizada)');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const publishNew = async () => {
    if (!window.confirm(
      `Se publicará una NUEVA versión de "${form.title}" (v${form.version}). La versión current actual quedará archivada. ¿Continuar?`
    )) return;
    setSaving(true);
    try {
      await api.post(`/admin/legal`, {
        doc_slug: activeSlug,
        title: form.title,
        body: form.body,
        version: form.version,
        is_provisional: form.is_provisional,
      });
      toast.success('Nueva versión publicada');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al publicar');
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración · Legal</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Documentos legales</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Edita el contenido y la versión de los documentos legales del sitio. Los cambios se aplican de inmediato en las páginas públicas y los futuros checkouts registran la nueva versión aceptada por el comprador.
            </p>
          </div>
          <nav className="flex gap-2 flex-wrap" data-testid="admin-legal-nav">
            {SLUGS.map((s) => (
              <button
                key={s.slug}
                data-testid={`admin-legal-tab-${s.slug}`}
                onClick={() => setActiveSlug(s.slug)}
                className={`px-3 py-2 text-sm font-semibold border transition-colors ${
                  activeSlug === s.slug
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'border-slate-300 hover:bg-slate-100'
                }`}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </div>

        {doc && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-6">
            <div className="border border-slate-200 bg-white p-5 space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Título</label>
                <Input
                  data-testid="admin-legal-title-input"
                  className="rounded-none mt-1"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Versión</label>
                  <Input
                    data-testid="admin-legal-version-input"
                    className="rounded-none mt-1 font-mono"
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                    placeholder="1.0 · 1.0-provisional · 2.0"
                  />
                  <p className="mt-1 text-xs text-slate-500">Formato: 1.0 · 1.0.1 · 1.0-provisional · 2.0-rc1</p>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Estado</label>
                  <label className="mt-2 flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="admin-legal-provisional-toggle"
                      checked={form.is_provisional}
                      onChange={(e) => setForm({ ...form, is_provisional: e.target.checked })}
                      className="h-4 w-4 accent-amber-600"
                    />
                    <span className="text-sm text-slate-700">Marcar como versión provisional</span>
                  </label>
                  <p className="mt-1 text-xs text-slate-500">Mostrará el aviso "en revisión jurídica" en la página pública.</p>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Contenido (Markdown compatible con GFM)
                </label>
                <Textarea
                  data-testid="admin-legal-body-input"
                  className="rounded-none mt-1 font-mono text-xs min-h-[500px]"
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Se soportan encabezados <code>## Sección</code>, listas, enlaces y <code>**negrita**</code>. La página pública renderiza este contenido automáticamente.
                </p>
              </div>
            </div>

            <aside className="border border-slate-200 bg-white p-5 h-fit space-y-4">
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <FileText className="h-4 w-4" /> Versión actual
              </div>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-500">Slug:</dt>
                  <dd className="font-mono">{doc.doc_slug}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-500">Versión:</dt>
                  <dd className="font-mono" data-testid="admin-legal-current-version">{doc.version}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-500">Provisional:</dt>
                  <dd>
                    {doc.is_provisional ? (
                      <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" /> Sí</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> No</span>
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-500">Vigente desde:</dt>
                  <dd className="text-xs">{doc.effective_at ? new Date(doc.effective_at).toLocaleString('es-CO') : '—'}</dd>
                </div>
                {doc.updated_at && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate-500">Última edición:</dt>
                    <dd className="text-xs">{new Date(doc.updated_at).toLocaleString('es-CO')}</dd>
                  </div>
                )}
              </dl>
              <Link
                to={SLUGS.find((s) => s.slug === activeSlug)?.publicPath || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-sm border border-slate-300 hover:bg-slate-100 px-3 py-2 font-semibold"
                data-testid="admin-legal-open-public"
              >
                Ver página pública ↗
              </Link>

              <div className="border-t border-slate-200 pt-4 space-y-2">
                <Button
                  onClick={saveInline}
                  disabled={saving}
                  data-testid="admin-legal-save-inline"
                  className="w-full rounded-none bg-slate-900 hover:bg-slate-800"
                >
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
                <p className="text-xs text-slate-500">
                  Guarda in-place: actualiza el título, contenido, versión y estado provisional sin crear una versión nueva.
                </p>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-2">
                <Button
                  onClick={publishNew}
                  disabled={saving}
                  variant="outline"
                  data-testid="admin-legal-publish-new"
                  className="w-full rounded-none"
                >
                  <RefreshCcw className="h-4 w-4 mr-2" /> Publicar nueva versión
                </Button>
                <p className="text-xs text-slate-500">
                  Usa esta opción cuando el abogado entregue el texto final: archiva la versión actual y publica una nueva. La historia queda registrada.
                </p>
              </div>
            </aside>
          </div>
        )}

        {doc && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section
              className="border border-slate-200 bg-white p-5"
              data-testid={`admin-legal-history-${activeSlug}`}
            >
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <History className="h-4 w-4" /> Historial de versiones
                <span className="text-xs text-slate-500 font-normal">({history.length})</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Todas las versiones publicadas de este documento, incluyendo las archivadas por "Publicar nueva versión".
              </p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Versión</th>
                      <th className="px-3 py-2">Provisional</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Vigente desde</th>
                      <th className="px-3 py-2">Última edición</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map((h, i) => (
                      <tr key={`${h.version}-${h.effective_at}-${i}`} data-testid={`admin-legal-history-row-${i}`}>
                        <td className="px-3 py-2 font-mono">{h.version}</td>
                        <td className="px-3 py-2">
                          {h.is_provisional ? (
                            <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" /> Sí</span>
                          ) : (
                            <span className="text-slate-500">No</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {h.is_current ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                              <CheckCircle2 className="h-3 w-3" /> Current
                            </span>
                          ) : (
                            <span className="text-slate-400">Archivada</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {h.effective_at ? new Date(h.effective_at).toLocaleString('es-CO') : '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {h.updated_at ? new Date(h.updated_at).toLocaleString('es-CO') : '—'}
                        </td>
                      </tr>
                    ))}
                    {history.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Sin historial</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section
              className="border border-slate-200 bg-white p-5"
              data-testid="admin-legal-export"
            >
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <FileDown className="h-4 w-4" /> Exportar aceptaciones
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Descarga en Excel o CSV las aceptaciones legales (registro y checkout), con auditoría de usuario, versión, IP y fecha. Aplica el filtro por documento (activo: <strong>{activeSlug}</strong>) y rango de fechas si lo necesitas.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Desde</label>
                  <Input
                    data-testid="admin-legal-export-date-from"
                    type="date"
                    className="rounded-none mt-1"
                    value={exportFilters.date_from}
                    onChange={(e) => setExportFilters({ ...exportFilters, date_from: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Hasta</label>
                  <Input
                    data-testid="admin-legal-export-date-to"
                    type="date"
                    className="rounded-none mt-1"
                    value={exportFilters.date_to}
                    onChange={(e) => setExportFilters({ ...exportFilters, date_to: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  data-testid="admin-legal-export-xlsx"
                  onClick={() => downloadXlsx('/admin/legal/acceptances/export', `aceptaciones_${activeSlug}.xlsx`, { format: 'xlsx', slug: activeSlug, ...exportFilters })}
                  className="rounded-none bg-emerald-700 hover:bg-emerald-600 text-white"
                >
                  <FileDown className="h-4 w-4 mr-2" /> Excel (.xlsx)
                </Button>
                <Button
                  data-testid="admin-legal-export-csv"
                  onClick={() => downloadCsv('/admin/legal/acceptances/export', `aceptaciones_${activeSlug}.csv`, { format: 'csv', slug: activeSlug, ...exportFilters })}
                  variant="outline"
                  className="rounded-none"
                >
                  <FileDown className="h-4 w-4 mr-2" /> CSV
                </Button>
                <Button
                  data-testid="admin-legal-export-xlsx-all"
                  onClick={() => downloadXlsx('/admin/legal/acceptances/export', `aceptaciones_todas.xlsx`, { format: 'xlsx', ...exportFilters })}
                  variant="outline"
                  className="rounded-none"
                >
                  <FileDown className="h-4 w-4 mr-2" /> Todas (Excel)
                </Button>
              </div>
            </section>
          </div>
        )}
      </div>
    </Layout>
  );
}
